<template>
    <div class="container-fluid">
        <div class="mb-3">
            <BreadcrumbNav />
        </div>
        <!-- Currently Editing Banner -->
        <div v-if="activityDetails && (activityDetails.activityid || activityDetails.activityname)" class="alert alert-success text-white d-flex align-items-center my-2" role="alert">
            <i class="material-icons me-2">edit</i>
            <div>
                <strong>Currently Editing (Activity):</strong>
                <span v-if="activityDetails.activityid" class="ms-2">Activity ID: <strong>{{ activityDetails.activityid }}</strong></span>
                <span v-if="activityDetails.activityname" class="ms-2">- <strong>{{ activityDetails.activityname }}</strong></span>
            </div>
        </div>

        <!-- Read-Only Banner -->
        <div v-if="readOnly" class="alert alert-warning text-white d-flex align-items-center my-2" role="alert">
            <i class="material-icons me-2">visibility</i>
            <div>
                <strong>View Only:</strong> You can view activity details but can only edit workgroups you administer.
            </div>
        </div>

        <!-- Tab Legend -->
        <div class="d-flex align-items-center gap-3 mb-2 text-xs text-secondary">
            <span><i class="material-icons text-danger" style="font-size: 12px; vertical-align: middle;">star</i> Required</span>
            <span><i class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i> Optional</span>
        </div>

        <NavTabs>
            <NavTab target="activity-details" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Activity Details (Required)">
                    Details <i class="material-icons text-danger" style="font-size: 12px; vertical-align: middle;">star</i>
                </span>
            </NavTab>

            <NavTab target="workgroups" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Workgroups (Required)">
                    Workgroups <i class="material-icons text-danger" style="font-size: 12px; vertical-align: middle;">star</i>
                </span>
            </NavTab>

            <NavTab target="recurrences" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Recurrences — Required for Auto-Generating Instances/Dates">
                    Recurrences <i class="material-icons text-danger" style="font-size: 12px; vertical-align: middle;">star</i>
                </span>
            </NavTab>

            <NavTab target="instances" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Instances/Dates (Optional)">
                    Instances <i class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i>
                </span>
            </NavTab>

            <NavTab target="preferred-users" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Manage Users Who Prefer This Activity (Optional)">
                    Preferred Users <i class="material-icons text-muted" style="font-size: 12px; vertical-align: middle;">radio_button_unchecked</i>
                </span>
            </NavTab>

            <NavTab target="activity-admins" :activeTab="activeTab" @update:activeTab="activeTab = $event">
                <span title="Manage Activity Admins (Required)">
                    Admins <i class="material-icons text-danger" style="font-size: 12px; vertical-align: middle;">star</i>
                </span>
            </NavTab>
        </NavTabs>

        <ContentContainer>
            <template #content>
                <ExplainPageButton :page-key="`activity.${activeTab}`" />
                <div v-if="activeTab === 'activity-details'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">View and edit the core settings for this activity, including name, schedule, and automation options.</p>
                    <ActivityDetails :readOnly="readOnly" />
                </div>

                <div v-else-if="activeTab === 'workgroups'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">Manage the workgroups (teams/roles) assigned to this activity. At least one workgroup is required.</p>
                    <Workgroups :readOnly="readOnly" />
                </div>

                <div v-else-if="activeTab === 'recurrences'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">Define how often this activity repeats. Recurrences are required for auto-generating instances/dates.</p>
                    <Recurrences :readOnly="readOnly" />
                </div>

                <div v-else-if="activeTab === 'instances'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">View, add, or manage specific dates and times (instances) for this activity.</p>
                    <Instances :readOnly="readOnly" />
                </div>

                <div v-else-if="activeTab === 'preferred-users'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">View and manage volunteers who have selected this activity as a preference.</p>
                    <PreferredUsers :readOnly="readOnly" />
                </div>

                <div v-else-if="activeTab === 'activity-admins'">
                    <p class="text-sm text-secondary mb-3 px-3 pt-3">Assign or remove administrators who can manage this activity.</p>
                    <ActivityAdmins :readOnly="readOnly" />
                </div>
            </template>
        </ContentContainer>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue"
import { useStore } from "vuex"
import { useRoute } from "vue-router"

import NavTab from "../../components/Tabs/NavTab.vue"
import NavTabs from "../../components/Tabs/NavTabs.vue"
import ContentContainer from "../../components/PageLayout/ContentContainer.vue"
import BreadcrumbNav from "../../components/common/BreadcrumbNav.vue"
import ExplainPageButton from "@/components/common/ExplainPageButton.vue"

import ActivityDetails from "./edit-activity/ActivityDetails.vue"
import Workgroups from "./edit-activity/Workgroups.vue"
import Recurrences from "./edit-activity/Recurrences.vue"
import Instances from "./edit-activity/Instances.vue"
import PreferredUsers from "./edit-activity/PreferredUsers.vue"
import ActivityAdmins from "./edit-activity/ActivityAdmins.vue"
import { usePermissions } from "@/composables/usePermissions"
import { useBreadcrumb } from "@/composables/useBreadcrumb"

const store = useStore()
const route = useRoute()
const validTabs = ["activity-details", "workgroups", "recurrences", "instances", "preferred-users", "activity-admins"]
const activeTab = ref(validTabs.includes(route.query.tab) ? route.query.tab : "activity-details")
const activityDetails = ref(null)

const { isReadOnlyForActivity } = usePermissions()
const readOnly = computed(() => isReadOnlyForActivity(route.params.activityid))

// Breadcrumb: set fallback trail for direct-URL access
const { trail, setTrail } = useBreadcrumb()

// Watch for changes in the store
const storeActivityDetails = computed(() => {
    return store.getters['editActivity/activityDetails'] || store.getters.activityDetails || null
})

watch(storeActivityDetails, (newValue) => {
    if (newValue) {
        activityDetails.value = newValue
    }
}, { immediate: true })

onMounted(async () => {
    const activityId = route.params.activityid
    if (!activityId) return
    // Only fetch if not already loaded (e.g. direct URL or refresh); edit-icon click fetches before navigate
    const existing = store.getters['editActivity/activityDetails'] || store.getters.activityDetails
    if (existing && String(existing.activityid) === String(activityId)) {
        // Ensure breadcrumb trail exists for direct-URL access
        if (!trail.value.length) {
            setTrail([
                { label: 'Activities', icon: 'list', route: { name: 'activities-events' } },
                { label: existing.activityname || `Activity #${activityId}`, icon: 'event', route: { name: 'editActivity', params: route.params } },
            ])
        }
        return
    }
    try {
        const data = await store.dispatch("fetchActivityDetails", activityId);
        if (data) {
            activityDetails.value = data
            if (!trail.value.length) {
                setTrail([
                    { label: 'Activities', icon: 'list', route: { name: 'activities-events' } },
                    { label: data.activityname || `Activity #${activityId}`, icon: 'event', route: { name: 'editActivity', params: route.params } },
                ])
            }
        }
    } catch (error) {
        console.error("Error loading activity:", error);
    }
})
</script>